import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { ConfigProvider, GAEventsProvider, Loader, ThemeProvider, NotificationContainer } from '@jjj/components';
import { ThemeProvider as MoveThemeProvider } from '@jjj/move';
import Routes from './router';
import { getCurrentCountryContext } from './helpers/countriesHelpers';
import { getPickerLocale } from './helpers/dateTime';
import DashAppHolder from './dashAppStyle';
import { languagesConfig } from './i18n/config';
import { GlobalStyle } from './global.style';
import events from './helpers/googleAnalytics/events';
import { getLanguageDirection, t } from './i18n';
import { getOrganizationCode } from './settings/organizationSettings';
import { RootState } from './redux/store';
import { waitForGilroyFonts } from './helpers/fontLoader';
import { logger } from './helpers/logger';
import appBootstrapActions from './redux/appBootstrap/actions';
import { useStoreReady } from './hooks/useStoreReady';

type Props = {
  loading: boolean;
  appBootstrap: () => void;
  theme: any;
  notificationData: {
    notifications: any[];
  };
  // Subscribed so the pickers' locale can follow it: the settings land after the first paint, and this is
  // the only thing that re-renders the provider when they do.
  countriesSettings: unknown;
};

const DashApp = (props: Props) => {
  const {
    loading,
    theme,
    appBootstrap,
    countriesSettings,
    notificationData: { notifications },
  } = props;
  const { i18n, ready } = useTranslation();
  const [fontsReady, setFontsReady] = useState(false);
  const { isStoreReady } = useStoreReady();

  useEffect(() => {
    appBootstrap();

    waitForGilroyFonts().then(hasGilroy => {
      setFontsReady(true);

      if (!hasGilroy) {
        logger.warn('App rendering without Gilroy fonts');
      }
    });

    document.body.classList.add(`${getOrganizationCode()}-org`);
  }, []);

  // Force rerender app when lang or country is changed
  const appKey = `${i18n.language}-${getCurrentCountryContext().code}`;
  const direction = getLanguageDirection();
  const isLoading = !ready || loading || !fontsReady;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pickerLocale = useMemo(() => getPickerLocale(), [countriesSettings]);

  return (
    <GAEventsProvider.Provider value={events}>
      <ConfigProvider direction={direction} locale={languagesConfig[i18n.language]?.locales.antd}>
        <ThemeProvider theme={theme}>
          <MoveThemeProvider locale={pickerLocale}>
            <GlobalStyle />
            <DashAppHolder>
              <Helmet htmlAttributes={{ lang: i18n.language, dir: direction }} defaultTitle={t('dash.dfw_title')} />
              {!isLoading || isStoreReady ? <Routes key={appKey} /> : <Loader />}
              <NotificationContainer notifications={notifications} />
            </DashAppHolder>
          </MoveThemeProvider>
        </ThemeProvider>
      </ConfigProvider>
    </GAEventsProvider.Provider>
  );
};

export default connect(
  (state: RootState) => ({
    theme: state.App.theme,
    loading: state.AppBootstrap.isLoading,
    notificationData: state.Notifications,
    countriesSettings: (state as any).CountriesSettings?.countriesSettings,
  }),
  dispatch => ({
    appBootstrap: () => dispatch(appBootstrapActions.appBootstrap()),
  }),
)(DashApp);
